import * as React from 'react';
import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";

const BreakdownChart = ({
  data,
  selectedMetrics,
  selectedBreakdown,
  onBreakdownChange,
  colors,
}) => {
  // Get available breakdowns
  const breakdowns = useMemo(() => {
    if (!data?.[0]?.data) return [];
    return Object.keys(data[0].data).filter((key) => key !== "Daily");
  }, [data]);

  // Transform data for the selected breakdown
  const chartData = useMemo(() => {
    if (!data?.[0]?.data || !selectedBreakdown) return [];
    const breakdownData = data[0].data[selectedBreakdown];
    return Object.entries(breakdownData)
      .filter(([key]) => key !== "glid")
      .map(([category, values]) => ({
        name: category,
        ...selectedMetrics.reduce(
          (acc, metric) => ({
            ...acc,
            [metric]: values[metric] || 0,
          }),
          {}
        ),
      }))
      .sort((a, b) => {
        // Sort by the first selected metric value in descending order
        const primaryMetric = selectedMetrics[0];
        return b[primaryMetric] - a[primaryMetric];
      });
  }, [data, selectedBreakdown, selectedMetrics]);

  // Calculate dynamic height based on number of bars
  const chartHeight = useMemo(() => {
    const barCount = chartData.length;
    // Allocate 40px per bar plus some padding
    const minHeight = 400;
    const calculatedHeight = Math.max(minHeight, barCount * 40 + 100);
    return calculatedHeight;
  }, [chartData]);

  // Format metric names for tooltip
  const getMetricName = (metric) => {
    return data[0].metrics[metric]?.display || metric;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Label>Breakdown By:</Label>
          <Select value={selectedBreakdown} onValueChange={onBreakdownChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select breakdown" />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              {breakdowns.map((breakdown) => (
                <SelectItem key={breakdown} value={breakdown}>
                  {breakdown.replace(/_/g, " ").toLowerCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {/* this extra container is added so we can make the chart scrollable */}
      <div className="h-[500px] overflow-y-auto">
        <div className="relative w-full" style={{ height: `${chartHeight}px` }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 20, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                type="number"
                tickFormatter={(value) =>
                  value >= 1000000
                    ? `${(value / 1000000).toFixed(1)}M`
                    : value >= 1000
                    ? `${(value / 1000).toFixed(1)}K`
                    : value
                }
              />
              <YAxis
                type="category"
                dataKey="name"
                width={90}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value, name) => [
                  value >= 1000000
                    ? `${(value / 1000000).toFixed(2)}M`
                    : value >= 1000
                    ? `${(value / 1000).toFixed(2)}K`
                    : value,
                  getMetricName(name),
                ]}
              />
              <Legend />
              {selectedMetrics.map((metric, index) => (
                <Bar
                  key={metric}
                  dataKey={metric}
                  fill={colors[index]}
                  name={getMetricName(metric)}
                  radius={[0, 4, 4, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default BreakdownChart;
